import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { BRANDING } from "@/config/branding";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ui, GoldButton } from "@/components/Primitives";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (password.length < 6) return setErr("Password must be at least 6 characters.");
    if (password !== confirm) return setErr("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/auth"), 1800);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md fade-up">
        <div className="font-display text-3xl gold-text tracking-wider text-center mb-6">{BRANDING.name}</div>
        <div className="glass-card rounded-3xl p-8">
          {done ? (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#8FB996]/15 flex items-center justify-center mx-auto mb-4"><CheckCircle2 size={24} className="text-[#5a9a6a]" /></div>
              <h1 className="font-display text-2xl font-light text-[#2C2A28] mb-2">Password updated</h1>
              <p className="text-[#8A7F70] text-sm">Redirecting you to sign in…</p>
            </div>
          ) : !token ? (
            <div className="text-center">
              <h1 className="font-display text-2xl font-light text-[#2C2A28] mb-2">Invalid link</h1>
              <p className="text-[#8A7F70] text-sm">This reset link is missing its token. <Link to="/forgot-password" className="gold-text underline">Request a new one</Link>.</p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-light text-[#2C2A28] mb-2">Choose a new password</h1>
              <p className="text-[#8A7F70] text-sm mb-6">Make it something memorable and secure.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label className={ui.label}>New password</Label>
                  <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={ui.input} placeholder="••••••••" />
                </div>
                <div>
                  <Label className={ui.label}>Confirm password</Label>
                  <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className={ui.input} placeholder="••••••••" />
                </div>
                {err && <div className="text-sm text-[#b06b6b] bg-[#C99FA0]/15 border border-[#C99FA0]/30 rounded-xl px-4 py-3">{err}</div>}
                <GoldButton data-testid="reset-submit" type="submit" disabled={loading} className="w-full h-12">
                  {loading ? <Loader2 className="animate-spin" size={20} /> : "Update password"}
                </GoldButton>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
